import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { css } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '@/styles';
import { useServiceTranslation } from '@/hooks/useServiceTranslation';
import api from '../../api';
import { showMedicalContentSelector } from '@/redux/config/selfData';
import { IGetResidentRes } from '../../types/api/resident/getResident';
import { ResidentItemHeader } from '../../components/residentItemHeader';
import { OutlineButton } from '../../components/outlineButton';
import { NavTabs } from './navTabs';
import { currentResidentSlice } from '../../redux/config/currentResident';
import { currentNursingHomeSelector, showLifeContentSelector } from '../../redux/config/currentNursingHome';
import { Loading } from '../../components/mui/Loading';

interface Props {
  url: string;
  params: { nursingHomeId: string; residentId: string };
  children: React.ReactNode;
}

interface Tab {
  label: string;
  href: string;
}

const Container = css`
  max-width: 1280px;
  background: #fff;
  border: var(--border-card);
  border-radius: 8px;
  margin: 40px auto;
`;

const HeaderContainer = css`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: ${color.white};
  border-radius: 8px;
  > :first-of-type {
    border-bottom: var(--border-card);
  }
`;

export const ResidentDetailLayout: React.FC<Props> = ({ children, params, url }) => {
  const { nursingHomeId, residentId } = params;
  const currentNursingHome = useSelector(currentNursingHomeSelector);
  const showLifeContent = useSelector(showLifeContentSelector);
  const showMedicalContent = useSelector(showMedicalContentSelector);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [tabNumber, setTabNumber] = React.useState(0);
  const [resident, setResident] = React.useState<IGetResidentRes>();
  const { t, i18n, serviceT } = useServiceTranslation();

  const medicalTabs = [
    { label: t('graph', 'グラフ'), href: `${url}/graph` },
    {
      label: serviceT('urination&ActivityTrendAnalysis'),
      href: `${url}/urine-activity-trend-analysis`,
    },
    { label: t('urinationDiary', '排尿日誌'), href: `${url}/diary` },
    { label: t('careHistory', 'ケア履歴'), href: `${url}/care-record` },
    { label: t('careHistorySummary', 'ケア履歴（集計）'), href: `${url}/care-record-data` },
    { label: t('bowelMovementCalendar', '排便カレンダー'), href: `${url}/defecation-analysis` },
    { label: '排泄支援情報', href: `${url}/life-care-plannings` },
  ];

  const professionalTabs = [
    { label: t('graph', 'グラフ'), href: `${url}/graph` },
    { label: t('careHistory', 'ケア履歴'), href: `${url}/care-record` },
    { label: t('careHistorySummary', 'ケア履歴（集計）'), href: `${url}/care-record-data` },
    { label: t('monthlyExcretionTable', '月間排泄表'), href: `${url}/monthly` },
    {
      label: t('urination&ActivityTrendAnalysis', '排尿・活動傾向'),
      href: `${url}/urine-activity-trend-analysis`,
    },
    { label: t('urinationDiaryU3', '排尿日誌 (U3)'), href: `${url}/u3diary` },
    { label: t('bowelMovementCalendar', '排便カレンダー'), href: `${url}/defecation-analysis` },
    { label: 'Ben' + t('graph', 'グラフ'), href: `${url}/benGraph` },
    { label: t('@skinCareTrends', '皮膚ケア傾向'), href: `${url}/ben-trend-analysis` },
    { label: '排泄支援情報', href: `${url}/life-care-plannings` },
  ];

  const fetchResident = React.useCallback(async () => {
    const res = await api.get('/residents/:residentId', { params: { residentId } });
    setResident(res);
    dispatch(currentResidentSlice.actions.fetch(res));
  }, []);

  const handleChangeTab = React.useCallback((tabNumber: number) => {
    setTabNumber(tabNumber);
  }, []);

  const handleGoEdit = React.useCallback(() => {
    history.push(`./edit`);
  }, []);

  const tabLinks: Tab[] = React.useMemo(() => {
    let tabs = showMedicalContent ? medicalTabs : professionalTabs;

    // 開発環境以外のみ表示
    if (process.env.REACT_APP_ENV_NAME !== 'development') {
      tabs = tabs.filter((tab) => tab.href !== `${url}/u3diary`);
    }

    if (!showLifeContent) {
      tabs = tabs.filter((tab) => tab.href !== `${url}/life-care-plannings`);
    }

    return tabs;
  }, [i18n, nursingHomeId, residentId, showLifeContent, showMedicalContent]);

  React.useEffect(() => {
    fetchResident();
  }, []);

  React.useEffect(() => {
    if (tabLinks.length <= 0) return;
    const index = tabLinks.findIndex((v) => v.href === location.pathname);
    if (index === -1) return;
    setTabNumber(index);
  }, [location, tabLinks]);

  return (
    <div css={Container}>
      {resident ? (
        <>
          <div css={HeaderContainer}>
            <ResidentItemHeader
              name={(resident.lastName ?? '') + (resident.name ?? '')}
              roomName={resident.roomName}
              unitName={resident.nursingHomeUnitName}
              rightElement={
                <OutlineButton
                  onClick={handleGoEdit}
                  icon={<img src='/images/icons/resident-setting.svg' alt='入居者設定' />}
                >
                  {t('edit', '編集')}
                </OutlineButton>
              }
            />
            {tabLinks.length > 0 && (
              <NavTabs tabsLink={tabLinks} selectedTabIndex={tabNumber} onChange={handleChangeTab} />
            )}
          </div>
          <div>{children}</div>
        </>
      ) : (
        <>
          <Loading />
        </>
      )}
    </div>
  );
};
